/**
 * Tuff Pupper AI support widget, launcher and panel shell.
 * Shadow DOM boundary so host theme CSS can never leak in or out, and this
 * widget never needs !important to win a specificity fight. Colors and fonts
 * still come from the theme's own CSS custom properties (inherited through
 * the shadow boundary, custom properties are not blocked by it), read off
 * the .color-scheme-2 wrapper the snippet renders around this element so
 * the widget stays visually consistent regardless of which of the theme's
 * 13 color schemes happens to be active on whatever page it's mounted on.
 * The header is deliberately scheme-3 (dark) regardless of ambient scheme,
 * so its literal values are used directly rather than inherited.
 *
 * The panel is a dialog on desktop (non-modal, no focus trap, sits in the
 * same corner the launcher used) and a true full-screen modal on mobile
 * (aria-modal, focus trap, page scroll locked, background content marked
 * inert). Escape closes at both breakpoints. The launcher hides while the
 * panel is open so the two never have to share the corner.
 *
 * Session identity travels as a client-generated id (crypto.randomUUID(),
 * persisted in localStorage under SESSION_STORAGE_KEY), sent as the
 * x-session-id request header on every call, not a cookie. The backend
 * lives on a different site (tuff-pupper-ai-support.tuff-pupper-ai.workers.dev)
 * than the storefront, so a cookie set there is third-party from the
 * storefront's point of view and gets blocked/evicted under Safari ITP and
 * similar tracking protection, silently resetting conversation memory.
 * localStorage on tuffpupper.com itself is first-party and unaffected.
 *
 * History loads once, the first time the panel actually becomes visible
 * (a real click, or a persisted open state on this exact page load), not
 * eagerly on connect. A visitor who never opens the widget costs the
 * backend nothing beyond the static script already sitting on the page.
 * When it does load, GET /history asks the Durable Object for the real
 * transcript rather than trusting any client-side state, since the DO is
 * the source of truth. Sending uses fetch + a ReadableStream reader rather
 * than EventSource, which can neither POST nor send custom headers.
 *
 * Waiting for a reply has three timers, all measured from send: typing dots
 * show immediately, a 5s soft timeout swaps them for a status line with no
 * change in the bubble's height, and an 11s hard timeout (time to first
 * token, not total request time) aborts and falls back to a plain error
 * line. Once the first token actually arrives both of those clear and a
 * separate 75s absolute cap takes over for the rest of the stream, so a
 * connection that never closes cannot hang the composer forever. A stream
 * that closes with zero text (a pure tool-call turn, or a moderation cut)
 * is treated as an error too, so the bubble never freezes on typing dots
 * with no error and no way to recover.
 *
 * Every failure mode gets its own plain copy, never a vendor name or a raw
 * error string: an outright rate limit (429) retries once silently before
 * showing anything, a hard timeout or a genuinely unreachable backend each
 * get their own line, and a drop mid-stream keeps whatever text already
 * arrived and offers Resend instead of throwing it away.
 *
 * A brand-new conversation (empty history) gets a scripted greeting and up
 * to four quick-reply chips instead of a blank panel. The greeting is
 * client-side only, never sent to the backend or persisted as a real turn,
 * it would otherwise pollute the message history sent to Claude on every
 * later call. Chips fire the exact same send path a typed message would;
 * "Something else" just focuses the input instead, free text stays live
 * alongside the chips rather than replacing them with a rigid menu.
 *
 * Open/closed state and any unsent draft persist to sessionStorage so a
 * reload or a click through to another page doesn't silently drop either.
 * A tiny blocking inline script in theme.liquid reads the open flag before
 * this file (deferred) even runs, so the very first shadow-DOM render
 * already reflects it, no separate open() call after the closed launcher
 * has already painted. A restored draft can't be submitted before the
 * history fetch that same open resolves, the submit handler waits on it,
 * so the transcript never renders out of order.
 *
 * Back/forward cache: a custom element only connects once, so returning to
 * this page via back/forward after bfcache freezes and restores it never
 * re-runs connectedCallback, the DOM just wakes back up as-is. A pageshow
 * listener with event.persisted true aborts any request that somehow
 * survived the freeze, wipes the panel body, and asks the Durable Object
 * for the real state again rather than trusting a stream that may be dead
 * or, worse, still alive and about to write into detached DOM. pagehide,
 * not the deprecated unload, does a final synchronous write of open state
 * and draft text, unload is a known bfcache-buster in several browsers and
 * every write here already happens synchronously anyway.
 */
(function () {
  if (customElements.get("tuff-chat-widget")) return;

  // The width-only threshold alone misses phone landscape: current iPhones
  // are 844-932px wide in landscape, well past 749.98px, which would flip
  // the panel out of its full-screen modal state (losing aria-modal, the
  // focus trap, the scroll lock, and the inert background) just from a
  // rotation mid-conversation. (max-height: 500px) and (pointer: coarse)
  // catches phone landscape (tallest current iPhone landscape height is
  // ~430px) without also matching a mouse-driven desktop window resized short.
  const MOBILE_QUERY = "(max-width: 749.98px), (max-height: 500px) and (pointer: coarse)";
  // Every bottom-anchored buy bar this theme can render on a PDP. Both go
  // full-width at bottom:0 on mobile. The launcher used to look only for
  // `.sticky-cta` and then hardcode that bar's height as a formula
  // (40px + --px-mobile + 2px). PupFlask PDPs render their own bar under a
  // deliberately different class (`pf-sticky-cta`, see the comment in
  // sections/main-product-pupflask.liquid explaining why it is kept out of
  // product-form.js's selector), so the check missed it outright and the
  // launcher sat on top of the buy bar. Measured live on tuffpupper.com at
  // 393x852 on 2026-08-19: bar 790-852, launcher 776-820, covering the
  // right quarter of the "Bottle only" button. Reading the real box height
  // instead of restating a formula fixes both bars and any future one.
  const STICKY_BAR_SELECTOR = ".pf-sticky-cta, .sticky-cta";
  // Breathing room between the launcher and whatever sits directly below
  // it, and between the launcher and the scroll-to-top button above it.
  const LAUNCHER_GAP_PX = 12;
  const API_BASE = "https://tuff-pupper-ai-support.tuff-pupper-ai.workers.dev";
  const SOFT_TIMEOUT_MS = 5000;
  const HARD_TIMEOUT_MS = 11000;
  const STREAM_ABSOLUTE_CAP_MS = 75000;
  const OPEN_STORAGE_KEY = "tuff-chat-open";
  const DRAFT_STORAGE_KEY = "tuff-chat-draft";
  const SESSION_STORAGE_KEY = "tuff-chat-session-id";
  // Only the store and the brand's own video host become links, so a reply never hands out an outside URL.
  const LINK_HOSTS = new Set(["tuffpupper.com", "www.tuffpupper.com", "m.media-amazon.com"]);
  // Replies render as plain text, so stray markdown bold would show its asterisks.
  const stripBold = (text) => text.replace(/\*\*(.+?)\*\*/gs, "$1");
  const ERROR_COPY = {
    unreachable:
      "I'm having trouble connecting right now. Try again in a moment, or email us at help@tuffpupper.com and we'll get back to you.",
    timeout:
      "That's taking longer than expected. Try again in a moment, or email us at help@tuffpupper.com and we'll get back to you.",
    busy: "We're getting a lot of questions right now. Try again in a minute, or email us at help@tuffpupper.com and we'll get back to you.",
    inProgress: "Your last message is still being handled. Give it a few seconds and try again.",
  };
  const GREETING_TEXT =
    "Hey, I'm Tuff Pupper's AI assistant. Ask me about sizing, orders, shipping, or anything else, and I'll do my best to help. If I can't, I'll get you to a real person fast.";
  const QUICK_REPLIES = [
    { label: "Order status", message: "I have a question about my order status." },
    { label: "Sizing help", message: "I need help finding the right size." },
    { label: "Shipping and returns", message: "I have a question about shipping or returns." },
    { label: "Something else", message: null },
  ];
  // Other components in this theme that lock background scroll the same
  // way (document.body.classList.add('overflow-hidden')): the cart drawer
  // (assets/cart-drawer.js), any modal-dialog/custom-popup instance, quick
  // add, popups (assets/global.js), the header nav drawer (menu-drawer.js /
  // header-drawer's own subclass), the header search modal
  // (assets/details-modal.js, a <details-modal><details>...</details-modal>
  // wrapper, state lives on the inner native <details open>, not the
  // wrapper), and the PDP pickup-availability drawer
  // (assets/pickup-availability.js, state is its own [open] attribute).
  // Releasing the shared lock must check all of these first, or
  // closing/opening this panel can strip a lock one of them still needs.
  //
  // .section-header, not .header: HeaderDrawer (assets/global.js) does
  // `this.header = this.header || document.querySelector('.section-header')`
  // and adds/removes 'menu-open' on that, the outer section wrapper
  // (class="section-header ..." per sections/header.liquid). The actual
  // <header class="header ..."> is a separate, nested child that never
  // carries 'menu-open' itself. `.header.menu-open` here never matched
  // anything, so closing this panel while the mobile nav drawer was open
  // would strip the drawer's own scroll lock out from under it.
  // Alert popups (data-is-alert, the "Selling fast" bar) open without taking the lock (global.js show()), so they never hold it.
  const OTHER_SCROLL_LOCK_SELECTOR =
    "cart-drawer.active, modal-dialog[open]:not([data-is-alert]), custom-popup[open]:not([data-is-alert]), .section-header.menu-open, details.menu-drawer-container[open], details-modal details[open], pickup-availability-drawer[open]";

  class RateLimitError extends Error {}
  class ReplyInProgressError extends Error {}

  function getOrCreateSessionId() {
    try {
      let id = window.localStorage.getItem(SESSION_STORAGE_KEY);
      if (!id) {
        id = crypto.randomUUID();
        window.localStorage.setItem(SESSION_STORAGE_KEY, id);
      }
      return id;
    } catch (error) {
      // localStorage unavailable (private mode, restricted context). An
      // in-memory id still lets this page load work, it just won't persist
      // across a reload. Nested try/catch: if the ORIGINAL failure was
      // crypto.randomUUID itself being unavailable rather than localStorage,
      // calling it again here would throw the identical error, uncaught,
      // which would abort connectedCallback before the shadow DOM ever
      // attaches, so the widget wouldn't render at all.
      try {
        return crypto.randomUUID();
      } catch (fallbackError) {
        return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      }
    }
  }

  // Height of the tallest bottom-anchored buy bar currently on the page, or
  // 0 when there is none. display:none means the bar genuinely is not on
  // this page (the ":last-child" rules in component-sticky-cta.css and
  // pupflask-pdp.css drop it when the product section is the last section,
  // since there would be nothing left to scroll to). visibility:hidden and
  // opacity:0 only mean "not revealed yet": those bars still occupy the
  // bottom of the viewport the moment the visitor scrolls into range, so
  // their box has to be cleared from the start. Reserving the space up
  // front also keeps the launcher still, a launcher that slides up and down
  // as the bar reveals would be worse than the overlap it fixes.
  function measureBottomBarHeight() {
    let tallest = 0;
    document.querySelectorAll(STICKY_BAR_SELECTOR).forEach((bar) => {
      if (getComputedStyle(bar).display === "none") return;
      const height = bar.getBoundingClientRect().height;
      if (height > tallest) tallest = height;
    });
    return Math.round(tallest);
  }

  // Zotabox's contact button, fixed in the same bottom-right corner on phones. It stays usable, so the
  // launcher stacks above it the way it already does on product pages.
  const CORNER_WIDGET_SELECTOR = ".zbx-trigger.ztb-smt-right";

  // Measured, not assumed, since Zotabox sets its own size and offset.
  function measureCornerWidgetReach() {
    if (!window.matchMedia(MOBILE_QUERY).matches) return 0;
    let reach = 0;
    document.querySelectorAll(CORNER_WIDGET_SELECTOR).forEach((widget) => {
      const style = getComputedStyle(widget);
      if (style.display === "none" || style.visibility === "hidden") return;
      const rect = widget.getBoundingClientRect();
      // Only a button pinned to the bottom-right corner shares the launcher's spot.
      if (rect.height === 0 || rect.left < window.innerWidth / 2 || window.innerHeight - rect.bottom > 48) return;
      reach = Math.max(reach, window.innerHeight - rect.top);
    });
    return Math.round(Math.max(0, reach));
  }

  // The launcher's own bottom offset, as a CSS length. Falls back to the
  // theme's mobile side padding plus the gap (what every template used
  // before) whenever there is no bar to clear.
  function clearanceFor(barHeight) {
    return barHeight > 0
      ? `${barHeight + LAUNCHER_GAP_PX}px`
      : `calc(var(--px-mobile, 2rem) + ${LAUNCHER_GAP_PX}px)`;
  }

  class TuffChatWidget extends HTMLElement {
    connectedCallback() {
      if (this.shadowRoot) return;

      this._sessionId = getOrCreateSessionId();
      // Only present for a signed-in shopper, lets a hand-off reach them without asking for an email.
      this._accountEmail = (this.getAttribute("data-account-email") || "").trim();
      // The product page this chat sits on, so "will this fit" has something to refer to.
      this._productHandle = (this.getAttribute("data-product-handle") || "").trim();
      this._pendingFocus = null;
      this._contactRecheck = false;
      this._activeController = null;
      this._historyController = null;
      this._historyPromise = null;
      this._closeTimer = null;

      const shouldStartOpen =
        typeof window.__tuffChatOpen === "boolean"
          ? window.__tuffChatOpen
          : this._readStorage(OPEN_STORAGE_KEY) === "1";
      const draftText = this._readStorage(DRAFT_STORAGE_KEY) || "";
      // Measured once here so the very first paint is already correct, then
      // kept current by _scheduleClearanceSync (fonts, rotation, resize, and
      // pupflask-pdp.js swapping in its two-button bundle bar all land after
      // this runs and can change the bar's height).
      const initialClearance = clearanceFor(measureBottomBarHeight());

      const shadow = this.attachShadow({ mode: "open" });
      shadow.innerHTML = `
        <style>
          :host {
            all: initial;
            /* all:initial resets -webkit-text-size-adjust to its spec
               initial value (auto) for everything inside this shadow root,
               even though layout/theme.liquid sets text-size-adjust: 100%
               on the page itself. With it back at "auto", iOS Safari can
               apply its own automatic text-inflation heuristic scoped to
               just this subtree, independent of the page's own zoom state,
               which is what made the whole widget (button text included,
               not only the input) render oversized on a real phone even in
               a fresh incognito tab. Restated explicitly here so it can't
               silently reappear. */
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
            /* Same all:initial reset, same fix pattern: WebKit's default
               translucent gray tap-highlight rectangle on every button tap
               (launcher, close, chips, send, resend), restated explicitly
               here so it inherits down through the shadow tree the same way
               font-family and color already do. */
            -webkit-tap-highlight-color: transparent;
            /* The measured height of whichever bottom buy bar this template
               renders, plus a 12px gap, or just the usual mobile side
               padding plus that gap when there's nothing to clear. See
               measureBottomBarHeight for why this is measured rather than
               restated as a formula. _applyClearance overwrites this as an
               inline custom property on the host whenever the bar's height
               changes, and an inline property beats this :host rule, so this
               value only ever needs to be right for the first paint. */
            --tuff-clearance-mobile: ${initialClearance};
            /* Desktop sticky-cta is centered and never reaches this corner,
               so the scroll-to-top button (bottom-right, --px-mobile + 40px)
               is the only thing to clear here, plus the same 12px gap,
               regardless of template. */
            --tuff-clearance-desktop: calc(var(--px-mobile, 2rem) + 40px + 12px);
          }
          .launcher {
            position: fixed;
            right: calc(16px + var(--temp-scrollbar-width, 0px));
            bottom: var(--tuff-clearance-mobile);
            /* Reported live 2026-08-14: a Klaviyo teaser popup (z-index
               90000) went full-width on mobile and visually buried this
               launcher underneath it (999 lost the stacking-order
               comparison outright, no selector fix could have helped, this
               is a different element entirely, not a specificity problem).
               This is a support entry point, not marketing chrome, it
               should stay reachable by default rather than lose that fight
               to whichever app happens to load with a higher number.
               999990 was raised again 2026-08-15 after a final review
               caught Zotabox's own contact-widget trigger
               (.zbx-trigger.ztb-smt-right, an enabled app embed, see
               component-sticky-cta.css) sitting at z-index 99999999 in the
               exact same bottom-right corner, confirmed live via
               elementsFromPoint at the launcher's own tap point: Zotabox's
               icon, link, and trigger container all sat above this widget
               in the real stacking order, making the launcher untappable.
               999999990 clears that with room to spare, short of chasing
               an arbitrary max-int war with something like Wheelio's
               2147483647 (suppressed separately, see
               tuff-popup-suppression.css and the Wheelio rule in this same
               file) or Zotabox's own lightbox holder (verified inert,
               z-index:-1, at 0 opacity, until Zotabox's own JS opens it,
               at which point it's an intentional full-screen takeover the
               visitor is actively using, not a passive floating trigger
               this widget needs to keep beating). */
            z-index: 999999990;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 44px;
            padding: 0 20px;
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: var(--buttons-radius-outset, 7px);
            background: rgb(var(--color-button, 206, 15, 45));
            color: rgb(var(--color-button-text, 243, 243, 243));
            font-family: var(--font-heading-family, Oswald, sans-serif);
            font-weight: 700;
            font-size: 1.4rem;
            letter-spacing: 0.03em;
            text-transform: uppercase;
            cursor: pointer;
            transition: transform 0.2s ease;
          }
          .launcher:hover {
            transform: scale(1.04);
          }
          .launcher__icon {
            display: none;
          }
          /* Icon only where the pill would sit over prices, swatches or a form button. */
          :host([data-compact="always"]) .launcher {
            width: 44px;
            padding: 0;
            border-radius: 50%;
          }
          :host([data-compact="always"]) .launcher__icon {
            display: block;
          }
          :host([data-compact="always"]) .launcher__label {
            display: none;
          }
          @media screen and (max-width: 749.98px) {
            :host([data-compact="mobile"]) .launcher {
              width: 44px;
              padding: 0;
              border-radius: 50%;
            }
            :host([data-compact="mobile"]) .launcher__icon {
              display: block;
            }
            :host([data-compact="mobile"]) .launcher__label {
              display: none;
            }
          }
          .launcher:focus-visible {
            outline: 2px solid rgb(var(--color-button, 206, 15, 45));
            outline-offset: 2px;
          }
          @media screen and (min-width: 750px) {
            .launcher {
              right: calc(24px + var(--temp-scrollbar-width, 0px));
              bottom: var(--tuff-clearance-desktop);
            }
          }

          .panel {
            position: fixed;
            right: calc(16px + var(--temp-scrollbar-width, 0px));
            bottom: var(--tuff-clearance-mobile);
            /* One above the launcher, same relative gap as before, see the
               launcher's own z-index comment for the full history,
               including the 2026-08-15 Zotabox bump. */
            z-index: 999999991;
            display: flex;
            flex-direction: column;
            width: 380px;
            max-width: calc(100vw - 32px);
            height: 600px;
            max-height: calc(100vh - 140px);
            background: rgb(var(--color-background, 255, 255, 255));
            color: rgb(var(--color-foreground, 18, 18, 18));
            border-radius: var(--buttons-radius-outset, 7px);
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
            overflow: hidden;
            /* Suppresses the native double-tap-to-zoom gesture on this
               full-screen surface. WebKit computes touch-action
               hierarchically, so setting it here on the container covers
               every descendant (header, body, composer) without repeating
               it per child. */
            touch-action: manipulation;
            opacity: 0;
            transform: translateY(8px);
            transition: opacity 0.18s ease, transform 0.18s ease;
          }
          .panel[hidden] {
            display: none;
          }
          .panel.is-open {
            opacity: 1;
            transform: translateY(0);
          }
          .panel:focus-visible {
            outline: none;
          }
          @media (prefers-reduced-motion: reduce) {
            .panel {
              transition: none;
            }
          }
          @media screen and (min-width: 750px) {
            .panel {
              right: calc(24px + var(--temp-scrollbar-width, 0px));
              bottom: var(--tuff-clearance-desktop);
            }
          }
          @media screen and (max-width: 749.98px), screen and (max-height: 500px) and (pointer: coarse) {
            .panel {
              inset: 0;
              width: 100%;
              /* iOS dvh tracks browser-chrome collapse (address/tab bar),
                 not the on-screen keyboard: the keyboard only shrinks
                 window.visualViewport, leaving a 100dvh fixed element at its
                 pre-keyboard size, so the composer can end up hidden behind
                 the keyboard. --tuff-vvh is set from visualViewport.height
                 on composer focus and on every visualViewport resize (see
                 _syncViewportHeight), mirroring the theme's own proven fix
                 for the identical problem (assets/predictive-search.js's
                 --bvh). Falls back to 100dvh whenever the keyboard isn't
                 open (property unset) or on browsers without visualViewport. */
              height: 100dvh;
              height: var(--tuff-vvh, 100dvh);
              /* Height alone is only half of it. To lift the focused input
                 above the keyboard iOS also SCROLLS the visual viewport
                 inside the layout viewport, and a position:fixed element
                 does not follow that: top:0 stays pinned to the layout
                 viewport, so the whole panel slides off the top of the
                 screen by exactly visualViewport.offsetTop and the
                 storefront shows through in the gap it leaves below the
                 composer. That is the band of hero image and "Shop
                 PupFlask" button visible under the message box for ~1.5s
                 after the keyboard opened in the 2026-08-19 screen
                 recording. --tuff-vvt puts the panel back onto the visible
                 viewport and is 0 whenever no keyboard is up, so this is
                 inert the rest of the time. */
              top: var(--tuff-vvt, 0px);
              max-width: none;
              max-height: none;
              border-radius: 0;
            }
          }

          .panel__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex: 0 0 auto;
            padding: 16px 12px 16px 20px;
            background: rgb(37, 39, 39);
            color: rgb(255, 255, 255);
          }
          .panel__header-text {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .panel__title {
            margin: 0;
            font-family: var(--font-heading-family, Oswald, sans-serif);
            font-weight: 700;
            font-size: 1.6rem;
            letter-spacing: 0.03em;
            text-transform: uppercase;
          }
          .ai-badge {
            display: inline-block;
            padding: 0.3em 0.7em;
            border-radius: var(--buttons-radius, 6px);
            background: rgb(206, 15, 45);
            color: rgb(243, 243, 243);
            font-family: var(--font-heading-family, Oswald, sans-serif);
            font-size: 1rem;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
          }
          .panel__close {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 44px;
            min-height: 44px;
            padding: 0;
            border: none;
            background: transparent;
            color: inherit;
            cursor: pointer;
          }
          .panel__close svg {
            width: 20px;
            height: 20px;
          }
          .panel__close:focus-visible {
            outline: 2px solid rgb(255, 255, 255);
            outline-offset: -4px;
          }

          .panel__body {
            flex: 1 1 auto;
            overflow-y: auto;
            /* The background scroll lock (document.body.overflow-hidden,
               _syncModalState) is the well-documented weaker iOS Safari
               technique and doesn't reliably stop a touch-driven
               rubber-band/scroll-chain from a nested scroll container.
               contain stops a swipe past the top/bottom of this list from
               bleeding into the page behind the full-screen panel. Matches
               the theme's own precedent for the identical problem on its
               scrollable search-results panel (component-predictive-search.css). */
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
            padding: 16px 20px;
          }
          .message {
            max-width: 80%;
            margin-bottom: 12px;
            padding: 10px 14px;
            border-radius: var(--buttons-radius, 6px);
            font-family: var(--font-body-family, inherit);
            font-size: 1.4rem;
            line-height: 1.4;
            white-space: pre-wrap;
            overflow-wrap: break-word;
          }
          .message--visitor {
            margin-left: auto;
            background: rgb(206, 15, 45);
            color: rgb(243, 243, 243);
          }
          .message--bot {
            margin-right: auto;
            background: rgb(243, 243, 243);
            color: rgb(18, 18, 18);
          }
          .message--bot a {
            color: rgb(206, 15, 45);
            text-decoration: underline;
            text-underline-offset: 2px;
          }
          .message--pending {
            /* Matches one line of real text so dots swapping to a status line,
               or a status line swapping to actual content, never resizes the
               bubble mid-wait. */
            min-height: 1.4em;
          }
          .message--error {
            background: rgb(243, 243, 243);
            color: rgb(178, 20, 20);
          }
          .product-cards {
            display: flex;
            flex-direction: column;
            gap: 6px;
            max-width: 92%;
            margin: -4px 0 12px;
          }
          .product-card {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px;
            border-radius: var(--buttons-radius, 6px);
            border: 1px solid rgb(224, 224, 224);
            background: rgb(255, 255, 255);
            text-decoration: none;
            color: inherit;
          }
          .product-card:hover,
          .product-card:focus-visible {
            border-color: rgb(206, 15, 45);
          }
          .product-card__image {
            width: 44px;
            height: 44px;
            border-radius: 4px;
            object-fit: cover;
            flex-shrink: 0;
            background: rgb(243, 243, 243);
          }
          .product-card__info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
          }
          .product-card__title {
            font-family: var(--font-body-family, inherit);
            font-size: 1.3rem;
            line-height: 1.3;
            color: rgb(18, 18, 18);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .product-card__price {
            font-family: var(--font-body-family, inherit);
            font-size: 1.2rem;
            color: rgb(206, 15, 45);
            font-weight: 600;
          }
          .typing-dots {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            height: 1.4em;
          }
          .typing-dots span {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: currentColor;
            opacity: 0.4;
            animation: tuff-typing-bounce 1.2s infinite ease-in-out;
          }
          .typing-dots span:nth-child(2) {
            animation-delay: 0.15s;
          }
          .typing-dots span:nth-child(3) {
            animation-delay: 0.3s;
          }
          @keyframes tuff-typing-bounce {
            0%, 60%, 100% {
              opacity: 0.4;
              transform: translateY(0);
            }
            30% {
              opacity: 1;
              transform: translateY(-3px);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .typing-dots span {
              animation: none;
              opacity: 0.7;
            }
          }
          .status-line {
            font-size: 1.3rem;
            font-style: italic;
            opacity: 0.75;
          }
          .resend-button {
            display: flex;
            align-items: center;
            min-height: 44px;
            margin-top: 8px;
            padding: 6px 16px;
            border: 1px solid rgb(206, 15, 45);
            border-radius: var(--buttons-radius, 6px);
            background: transparent;
            color: rgb(206, 15, 45);
            font-family: var(--font-body-family, inherit);
            font-size: 1.2rem;
            font-weight: 700;
            cursor: pointer;
          }
          .resend-button:focus-visible {
            outline: 2px solid rgb(206, 15, 45);
            outline-offset: 2px;
          }
          .quick-replies {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin: 4px 0 12px;
          }
          .quick-reply-chip {
            /* Base look matches the real variant-pill component
               (component-product-variant-picker.css): transparent background,
               foreground-tinted ring, same padding and radius token. Ring
               alpha is 0.5, not the pill's own 0.2, that ratio only clears
               ~1.56:1 against a white panel, well under WCAG 1.4.11's 3:1
               minimum for a UI-component boundary; 0.5 clears it with margin. */
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 44px;
            padding: 0.7em 1.5em;
            border: none;
            border-radius: var(--buttons-radius, 6px);
            background: rgb(var(--color-background, 255, 255, 255));
            color: rgb(var(--color-foreground, 18, 18, 18));
            box-shadow: 0 0 0 1px rgba(var(--color-foreground, 18, 18, 18), 0.5);
            font-family: var(--font-body-family, inherit);
            font-size: 1.3rem;
            cursor: pointer;
            transition: box-shadow 0.2s ease, color 0.2s ease;
          }
          .quick-reply-chip:hover {
            box-shadow: 0 0 0 1px rgb(206, 15, 45);
            color: rgb(206, 15, 45);
          }
          .quick-reply-chip:focus-visible {
            outline: 2px solid rgb(206, 15, 45);
            outline-offset: 2px;
          }
          .visually-hidden {
            position: absolute;
            overflow: hidden;
            width: 1px;
            height: 1px;
            margin: -1px;
            padding: 0;
            border: 0;
            clip: rect(0 0 0 0);
            white-space: nowrap;
          }

          .panel__composer {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 0 0 auto;
            padding: 12px;
            border-top: 1px solid rgba(0, 0, 0, 0.08);
          }
          .composer__input {
            flex: 1 1 auto;
            min-height: 44px;
            padding: 0 12px;
            /* 0.5 alpha, not the original 0.15 (~1.41:1 against a white
               panel), this is the input's only boundary cue and needs the
               same 3:1 WCAG 1.4.11 minimum as the chip ring above. */
            border: 1px solid rgba(0, 0, 0, 0.5);
            border-radius: var(--buttons-radius, 6px);
            font-family: var(--font-body-family, inherit);
            /* Hardcoded px, not rem: this theme sets html { font-size: calc(
               var(--font-body-scale) * 62.5%) }, a 10px root at the default
               scale, so 1.4rem here would compute to 14px. Any text input
               under 16px makes iOS Safari (and WebKit-based browsers on
               iOS, which is all of them) zoom the entire page on focus,
               reported live on a real phone. 16px is the documented safe
               floor and needs to stay a fixed unit here regardless of what
               the theme's root scale is set to, rem would silently
               reintroduce this the moment that scale changes. */
            font-size: 16px;
            color: rgb(var(--color-foreground, 18, 18, 18));
            background: rgb(var(--color-background, 255, 255, 255));
          }
          .composer__input:focus-visible {
            outline: 2px solid rgb(var(--color-button, 206, 15, 45));
            outline-offset: 2px;
          }
          .composer__send {
            flex: 0 0 auto;
            min-width: 44px;
            min-height: 44px;
            padding: 0 16px;
            border: none;
            border-radius: var(--buttons-radius-outset, 7px);
            background: rgb(var(--color-button, 206, 15, 45));
            color: rgb(var(--color-button-text, 243, 243, 243));
            font-family: var(--font-heading-family, Oswald, sans-serif);
            font-weight: 700;
            font-size: 1.3rem;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            cursor: pointer;
          }
          .composer__send:focus-visible {
            outline: 2px solid rgb(var(--color-button, 206, 15, 45));
            outline-offset: 2px;
          }
          .composer__input:disabled,
          .composer__send:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .contact-form {
            margin: 0 0 12px;
            padding: 12px;
            border: 1px solid rgba(0, 0, 0, 0.5);
            border-radius: var(--buttons-radius, 6px);
            background: rgb(var(--color-background, 255, 255, 255));
          }
          .contact-form__label {
            display: block;
            margin-bottom: 8px;
            font-family: var(--font-body-family, inherit);
            font-size: 1.3rem;
            font-weight: 700;
            color: rgb(var(--color-foreground, 18, 18, 18));
          }
          .contact-form__row {
            display: flex;
            gap: 8px;
          }
          .contact-form__input {
            flex: 1 1 auto;
            min-width: 0;
            min-height: 44px;
            padding: 0 12px;
            border: 1px solid rgba(0, 0, 0, 0.5);
            border-radius: var(--buttons-radius, 6px);
            font-family: var(--font-body-family, inherit);
            /* 16px keeps iOS from zooming on focus, same as the composer input. */
            font-size: 16px;
            color: rgb(var(--color-foreground, 18, 18, 18));
            background: rgb(var(--color-background, 255, 255, 255));
          }
          .contact-form__input[aria-invalid="true"] {
            border-color: rgb(206, 15, 45);
          }
          .contact-form__input:focus-visible,
          .contact-form__submit:focus-visible {
            outline: 2px solid rgb(var(--color-button, 206, 15, 45));
            outline-offset: 2px;
          }
          .contact-form__submit {
            flex: 0 0 auto;
            min-width: 44px;
            min-height: 44px;
            padding: 0 16px;
            border: none;
            border-radius: var(--buttons-radius-outset, 7px);
            background: rgb(var(--color-button, 206, 15, 45));
            color: rgb(var(--color-button-text, 243, 243, 243));
            font-family: var(--font-heading-family, Oswald, sans-serif);
            font-weight: 700;
            font-size: 1.3rem;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            cursor: pointer;
          }
          .contact-form__submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .contact-form__status {
            margin: 8px 0 0;
            font-family: var(--font-body-family, inherit);
            font-size: 1.3rem;
            color: rgb(var(--color-foreground, 18, 18, 18));
          }
          .contact-form__status:empty {
            display: none;
          }
        </style>
        <button
          type="button"
          class="launcher"
          aria-label="Chat with support"
          aria-haspopup="dialog"
          aria-expanded="${shouldStartOpen ? "true" : "false"}"
          style="${shouldStartOpen ? "display: none;" : ""}"
        >
          <svg class="launcher__icon" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm3 6.5a1.5 1.5 0 1 0 0 .01Zm5 0a1.5 1.5 0 1 0 0 .01Zm5 0a1.5 1.5 0 1 0 0 .01Z"/></svg>
          <span class="launcher__label">Support</span>
        </button>
        <div class="panel${shouldStartOpen ? " is-open" : ""}" role="dialog" aria-labelledby="tuff-chat-panel-title" tabindex="-1" ${shouldStartOpen ? "" : "hidden"}>
          <div class="panel__header">
            <div class="panel__header-text">
              <h2 class="panel__title" id="tuff-chat-panel-title">Support</h2>
              <span class="ai-badge">AI Assistant</span>
            </div>
            <button type="button" class="panel__close" aria-label="Close chat">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 512 512">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M368 368L144 144M368 144L144 368"/>
              </svg>
            </button>
          </div>
          <div class="panel__body" role="log" aria-busy="false"></div>
          <div class="visually-hidden" aria-live="polite" id="tuff-chat-status"></div>
          <form class="panel__composer">
            <label class="visually-hidden" for="tuff-chat-input">Type a message</label>
            <input
              type="text"
              id="tuff-chat-input"
              class="composer__input"
              autocomplete="off"
              enterkeyhint="send"
              placeholder="Type your message"
              required
            />
            <button type="submit" class="composer__send">Send</button>
          </form>
        </div>
      `;

      this._launcher = shadow.querySelector(".launcher");
      this._panel = shadow.querySelector(".panel");
      this._closeButton = shadow.querySelector(".panel__close");
      this._body = shadow.querySelector(".panel__body");
      this._statusRegion = shadow.querySelector("#tuff-chat-status");
      this._composer = shadow.querySelector(".panel__composer");
      this._input = shadow.querySelector(".composer__input");
      this._sendButton = shadow.querySelector(".composer__send");
      this._mobileQuery = window.matchMedia(MOBILE_QUERY);

      this._launcher.addEventListener("click", () => {
        this.openPanel();
        this._guardOpenFocus();
      });
      this._closeButton.addEventListener("click", () => this.closePanel());
      this._panel.addEventListener("keydown", (event) => this._handlePanelKeydown(event));
      this._mobileQuery.addEventListener("change", () => this._syncModalState());
      this._composer.addEventListener("submit", (event) => this._handleSubmit(event));
      // WebKit (desktop Safari without Full Keyboard Access, and every iOS
      // browser, all WebKit under the hood) never focuses a button on
      // click/tap the way Chromium and Firefox do. Without this, tapping
      // Send on an iPhone leaves shadowRoot.activeElement as neither the
      // input nor the button by the time _sendAndStream runs, so the
      // hadComposerFocus check there reads false and the focus-parking fix
      // for the pending window never engages on exactly the platform it's
      // for. preventDefault on pointerdown suppresses the browser's default
      // focus shift without blocking the click/submit that follows, so the
      // input simply keeps focus through the whole interaction everywhere.
      this._sendButton.addEventListener("pointerdown", (event) => event.preventDefault());
      this._input.addEventListener("input", () => {
        this._writeStorage(DRAFT_STORAGE_KEY, this._input.value);
      });
      // iOS dvh tracks browser-chrome collapse, not the on-screen keyboard,
      // which only shrinks window.visualViewport, leaving a 100dvh
      // full-screen panel at its pre-keyboard size and the composer
      // potentially hidden behind the keyboard. The theme already solved
      // this exact problem for its own search modal (predictive-search.js's
      // --bvh, staggered 100/500ms re-reads on input focus since the
      // keyboard's open animation isn't instant), this mirrors that proven
      // pattern scoped to this component instead of relying on the site's
      // shared value, which only refreshes on load/orientationchange/a
      // custom resize event, none of which reliably fire for a
      // keyboard-only visual-viewport change.
      if (window.visualViewport) {
        this._input.addEventListener("focus", () => this._syncViewportHeight());
        // The keyboard closing is animated too, and dismissing it by tapping
        // the message list fires blur before the viewport finishes growing
        // back. Without this the panel can sit short until the next resize.
        this._input.addEventListener("blur", () => this._syncViewportHeight());
        window.visualViewport.addEventListener("resize", () => this._syncViewportHeight());
        // offsetTop changes on visual-viewport scroll, not resize, so the
        // vertical correction above needs its own listener. Cheap enough to
        // run raw: it writes two custom properties and nothing else.
        window.visualViewport.addEventListener("scroll", () => this._applyViewportMetrics());
      }

      this._clearanceFrame = null;
      this._trackFrame = null;
      this._trackUntil = 0;
      this._syncClearanceNow();
      window.addEventListener("resize", () => this._scheduleClearanceSync());
      window.addEventListener("orientationchange", () => this._scheduleClearanceSync());
      // The bars are server-rendered, but their final height depends on the
      // heading webfont and, on PupFlask PDPs, on pupflask-pdp.js swapping
      // in the two-button bundle layout. Both land after this script.
      window.addEventListener("load", () => this._scheduleClearanceSync());
      this._watchCornerWidget();
      if (window.ResizeObserver) {
        const barObserver = new ResizeObserver(() => this._scheduleClearanceSync());
        document.querySelectorAll(STICKY_BAR_SELECTOR).forEach((bar) => barObserver.observe(bar));
      }
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) this._recoverFromBfcache();
      });
      window.addEventListener("pagehide", () => {
        // OPEN_STORAGE_KEY is deliberately not rewritten here. this._panel.hidden
        // lags a close click by up to 180ms (closePanel's own transition
        // timer), so reading it here could observe the pre-close state and
        // overwrite the correct "0" that closePanel() already wrote
        // synchronously the instant the visitor clicked, silently reopening
        // the widget on the next page load. openPanel()/closePanel() are the
        // only two places this value should ever be written.
        this._writeStorage(DRAFT_STORAGE_KEY, this._input.value);
      });

      if (draftText) this._input.value = draftText;

      // History is only ever fetched once the panel is actually visible, same
      // principle as the SSE chat connection only opening on send: a visitor
      // who never opens the widget should cost the backend nothing beyond the
      // static script already deferred on the page.
      this._historyLoaded = false;

      // Panel already starts open per the persisted flag (baked into the
      // markup above), just line up aria-modal/scroll-lock/inert for the
      // current breakpoint. Skips the focus-into-input openPanel() does on a
      // real click, restoring a session shouldn't steal focus from the page.
      // It's visible immediately, so history loads immediately too.
      if (shouldStartOpen) {
        this._syncModalState();
        this._syncViewportHeight();
        this._historyLoaded = true;
        this._historyPromise = this._loadHistory();
      }
    }

    // A bfcache restore wakes the exact same DOM back up, including any bubble
    // still showing typing dots or a status line for a request that may or
    // may not have actually been killed while frozen (bfcache implementations
    // vary: some hard-abort in-flight fetches on freeze, some suspend and
    // resume them on restore). Abort defensively rather than assume, a
    // resumed promise chain touching this now-replaced DOM would otherwise
    // silently attach a Resend button to an invisible node or steal focus.
    // Only relevant if the panel was actually open when the page froze, a
    // closed panel has no stuck state to clean up.
    _recoverFromBfcache() {
      if (this._panel.hidden) return;
      // A close click just before the freeze (panel.hidden still false
      // during its own 180ms transition) leaves _closeTimer scheduled. This
      // restore is about to treat the panel as open and repopulate it, so an
      // uncleared timer from before the freeze would fire moments later and
      // silently hide the panel this same call just restored, on top of
      // wasting the history fetch below. Same clear-before-proceeding this
      // needs already used in openPanel().
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
      if (this._activeController) {
        this._activeController.abort();
        this._activeController = null;
      }
      if (this._historyController) {
        this._historyController.abort();
        this._historyController = null;
      }
      this._input.disabled = false;
      this._sendButton.disabled = false;
      this._body.replaceChildren();
      this._historyLoaded = true;
      this._historyPromise = this._loadHistory();
      // The viewport (and possibly the breakpoint, on a rotation while
      // frozen) can have changed while the page was in bfcache.
      this._syncViewportHeight();
    }

    _readStorage(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    _writeStorage(key, value) {
      try {
        if (value) {
          sessionStorage.setItem(key, value);
        } else {
          sessionStorage.removeItem(key);
        }
      } catch (error) {
        // Storage unavailable (private browsing, restricted context). Open
        // state and drafts just won't persist, not worth surfacing to the user.
      }
    }

    async _loadHistory() {
      // Own AbortController, separate from _activeController (which only
      // ever tracks the /chat stream), so a bfcache restore can actually
      // cancel a /history request that's still in flight, matching what the
      // file's own docblock already claims happens for any in-flight request.
      const controller = new AbortController();
      this._historyController = controller;

      let turns;
      try {
        const response = await fetch(`${API_BASE}/history`, {
          signal: controller.signal,
          headers: { "x-session-id": this._sessionId },
        });
        this._syncSessionId(response);
        if (!response.ok) {
          // Backend hiccup (cold start, transient network error). Fall back
          // to the greeting, which is designed to work standalone, rather
          // than leaving the panel permanently blank. Body is cleared first
          // so a retry on the next open never stacks a second greeting on
          // top of this one.
          this._historyLoaded = false;
          this._body.replaceChildren();
          this._renderGreeting();
          return;
        }
        turns = await response.json();
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("tuff-chat-widget: failed to load history", error);
        this._historyLoaded = false;
        this._body.replaceChildren();
        this._renderGreeting();
        return;
      } finally {
        if (this._historyController === controller) this._historyController = null;
      }
      if (Array.isArray(turns) && turns.length > 0) {
        // The server's copy is complete, so a retry after an earlier failed load never stacks a second transcript.
        this._body.replaceChildren();
        this._renderHistory(turns);
        this._checkContactStatus();
      } else {
        this._body.replaceChildren();
        this._renderGreeting();
      }
    }

    // The backend echoes the session id it actually used, defensive sync in
    // case ours was ever rejected or a fresh one had to be minted server-side
    // (e.g. localStorage was unavailable and this page load's in-memory id
    // wasn't recognized). A no-op on the normal path where they already match.
    _syncSessionId(response) {
      const serverSessionId = response.headers.get("x-session-id");
      if (serverSessionId && serverSessionId !== this._sessionId) {
        this._sessionId = serverSessionId;
        try {
          window.localStorage.setItem(SESSION_STORAGE_KEY, serverSessionId);
        } catch (error) {
          // Ignore, same as every other storage write in this file.
        }
      }
    }

    _renderGreeting() {
      this._body.appendChild(this._buildMessageElement("bot", GREETING_TEXT).element);
      this._renderQuickReplies();
      this._body.scrollTop = this._body.scrollHeight;
    }

    _renderQuickReplies() {
      const wrapper = document.createElement("div");
      wrapper.className = "quick-replies";

      for (const reply of QUICK_REPLIES) {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "quick-reply-chip";
        chip.textContent = reply.label;
        chip.addEventListener("click", () => {
          if (this._input.disabled || this._historyController) return;
          // Focus before removal for every chip, not just "Something else":
          // removing the currently-focused element from the DOM moves focus
          // to document.body, outside the shadow root and outside the focus
          // trap, silently breaking keyboard navigation on mobile.
          this._input.focus();
          wrapper.remove();
          if (reply.message) {
            this._body.appendChild(this._buildMessageElement("visitor", reply.message).element);
            this._sendAndStream(reply.message);
          }
        });
        wrapper.appendChild(chip);
      }

      this._body.appendChild(wrapper);
    }

    _renderHistory(turns) {
      const fragment = document.createDocumentFragment();
      for (const turn of turns) {
        const text = turn.role === "bot" ? stripBold(turn.text) : turn.text;
        const { element, textNode } = this._buildMessageElement(turn.role, text);
        if (turn.role === "bot") this._linkify(textNode);
        fragment.appendChild(element);
        // Saved with the reply, so a reload shows the same cards the reply streamed.
        if (turn.role === "bot" && Array.isArray(turn.cards)) this._renderProductCards(turn.cards, element);
      }
      this._body.appendChild(fragment);
      this._body.scrollTop = this._body.scrollHeight;
    }

    _buildMessageElement(role, text) {
      const message = document.createElement("div");
      message.className = `message message--${role}`;

      // Color and side are enough visually, but a screen reader needs the
      // sender said out loud too, not just inferred from position or color.
      const sender = document.createElement("span");
      sender.className = "visually-hidden";
      sender.textContent = role === "visitor" ? "You said: " : "Support said: ";
      message.appendChild(sender);

      const textNode = document.createTextNode(text);
      message.appendChild(textNode);

      return { element: message, textNode };
    }

    // A partial reply may end mid address, so its last address stays text.
    _linkify(textNode, partial = false) {
      const text = textNode.textContent;
      const fragment = document.createDocumentFragment();
      let last = 0;
      for (const match of text.matchAll(/https:\/\/[^\s<>"'“”‘’…()[\]*]+/g)) {
        if (partial && match.index + match[0].length === text.length) continue;
        const raw = match[0].replace(/[.,;:!?…”’*_]+$/, "");
        let url;
        try {
          url = new URL(raw);
        } catch {
          continue;
        }
        if (!LINK_HOSTS.has(url.hostname)) continue;
        fragment.append(text.slice(last, match.index));
        const link = document.createElement("a");
        link.href = url.href;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        // The video address is a long stream file name, a label reads better than the raw URL.
        link.textContent = url.pathname.endsWith(".m3u8") ? "Watch the video" : raw;
        fragment.append(link);
        last = match.index + raw.length;
      }
      if (last === 0) return;
      fragment.append(text.slice(last));
      textNode.replaceWith(fragment);
    }

    // After the bubble, not inside it, so cards get the full panel width. The Worker sends one card frame per reply.
    _renderProductCards(products, botElement) {
      if (!Array.isArray(products) || products.length === 0) return;

      const container = document.createElement("div");
      container.className = "product-cards";

      for (const product of products) {
        if (!product || typeof product.title !== "string" || typeof product.url !== "string") continue;

        const card = document.createElement("a");
        card.className = "product-card";
        card.href = product.url;
        card.target = "_blank";
        card.rel = "noopener noreferrer";

        if (typeof product.image === "string" && product.image) {
          const img = document.createElement("img");
          img.className = "product-card__image";
          img.src = product.image;
          img.alt = "";
          img.loading = "lazy";
          card.appendChild(img);
        }

        const info = document.createElement("div");
        info.className = "product-card__info";

        const title = document.createElement("span");
        title.className = "product-card__title";
        title.textContent = product.title;
        info.appendChild(title);

        if (typeof product.price_min === "number") {
          const price = document.createElement("span");
          price.className = "product-card__price";
          price.textContent =
            typeof product.price_max === "number" && product.price_max > product.price_min
              ? `From $${product.price_min.toFixed(2)}`
              : `$${product.price_min.toFixed(2)}`;
          info.appendChild(price);
        }

        card.appendChild(info);
        container.appendChild(card);
      }

      if (container.children.length === 0) return;
      botElement.insertAdjacentElement("afterend", container);
    }

    // One box at a time, always at the bottom of the conversation. Returns the input so the caller can focus it.
    // ref names the hand-off the box answers, sent back so the email lands on that ticket.
    _renderContactForm(ref = null) {
      // Keep a box already on screen, so a second request never wipes what the visitor typed.
      const previous = this._body.querySelector(".contact-form");
      if (previous) {
        if (ref) previous.dataset.ref = ref;
        this._body.appendChild(previous);
        this._body.scrollTop = this._body.scrollHeight;
        return previous.querySelector(".contact-form__input");
      }

      const form = document.createElement("form");
      form.className = "contact-form";
      form.noValidate = true;
      if (ref) form.dataset.ref = ref;

      const label = document.createElement("label");
      label.className = "contact-form__label";
      label.htmlFor = "tuff-chat-contact-email";
      label.textContent = "Leave your email so the team can get back to you";

      const row = document.createElement("div");
      row.className = "contact-form__row";

      const input = document.createElement("input");
      input.id = "tuff-chat-contact-email";
      input.className = "contact-form__input";
      input.type = "email";
      input.name = "email";
      input.autocomplete = "email";
      input.inputMode = "email";
      input.enterKeyHint = "send";
      input.maxLength = 254;
      input.required = true;
      input.placeholder = "you@example.com";

      const button = document.createElement("button");
      button.type = "submit";
      button.className = "contact-form__submit";
      button.textContent = "Send";
      // Same WebKit focus fix as the composer's Send button.
      button.addEventListener("pointerdown", (event) => event.preventDefault());

      const status = document.createElement("p");
      status.className = "contact-form__status";
      status.setAttribute("aria-live", "polite");

      row.append(input, button);
      form.append(label, row, status);
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        this._submitContact(form, input, button, status);
      });

      this._body.appendChild(form);
      this._body.scrollTop = this._body.scrollHeight;
      return input;
    }

    async _submitContact(form, input, button, status) {
      if (button.disabled) return;
      const email = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email)) {
        input.setAttribute("aria-invalid", "true");
        status.textContent = "Please enter a valid email address.";
        input.focus();
        return;
      }
      input.removeAttribute("aria-invalid");
      // readOnly, not disabled: disabling the focused field would push focus outside the panel.
      input.readOnly = true;
      if (this.shadowRoot.activeElement === button) input.focus();
      button.disabled = true;
      status.textContent = "Sending…";

      const fail = (message) => {
        input.readOnly = false;
        button.disabled = false;
        status.textContent = message;
        if (!form.isConnected) this._body.appendChild(this._buildMessageElement("bot", message).element);
      };

      // The ticket is saved just after the reply finishes, so a very quick submit can arrive first.
      for (let attempt = 0; attempt < 4; attempt++) {
        let response;
        try {
          response = await fetch(`${API_BASE}/contact`, {
            method: "POST",
            headers: { "content-type": "application/json", "x-session-id": this._sessionId },
            body: JSON.stringify(form.dataset.ref ? { email, ref: form.dataset.ref } : { email }),
          });
        } catch (error) {
          fail("That didn't go through. Try again, or email help@tuffpupper.com.");
          return;
        }

        if (response.ok) {
          const result = await response.json().catch(() => null);
          const confirmation = this._buildMessageElement(
            "bot",
            result?.result === "already_had_email"
              ? "Thanks, the team already has an email for this chat."
              : `Thanks, the team will reply to ${email}.`,
          ).element;
          // Removing the focused box would drop focus outside the panel, so hand it to the composer.
          const boxHadFocus = this.shadowRoot.activeElement === input || this.shadowRoot.activeElement === button;
          if (form.isConnected) {
            form.replaceWith(confirmation);
          } else {
            // The panel was redrawn mid-submit; a fresh box for the same hand-off would ask again.
            this._body.querySelectorAll(".contact-form").forEach((box) => box.remove());
            this._body.appendChild(confirmation);
          }
          if (boxHadFocus) (this._input.disabled ? this._panel : this._input).focus();
          this._body.scrollTop = this._body.scrollHeight;
          return;
        }

        if (response.status === 409 && attempt < 3) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          continue;
        }
        if (response.status === 409) {
          fail("Thanks. To make sure the team can reach you, please email help@tuffpupper.com.");
          return;
        }
        if (response.status === 400) {
          const body = await response.json().catch(() => null);
          input.setAttribute("aria-invalid", "true");
          fail(body?.error === "store_address" ? "Please enter your own email address." : "Please enter a valid email address.");
          return;
        }
        fail(response.status === 429 ? ERROR_COPY.busy : "That didn't go through. Try again, or email help@tuffpupper.com.");
        return;
      }
    }

    // Brings the box back while a hand-off from this chat lacks an email, and settles one on screen after a reply.
    // Returns true when it changed what the visitor sees.
    async _checkContactStatus(mode = "history") {
      try {
        const response = await fetch(`${API_BASE}/contact-status`, {
          headers: { "x-session-id": this._sessionId },
        });
        if (!response.ok) return false;
        const status = await response.json();
        const existing = this._body.querySelector(".contact-form");
        if (mode === "after-reply") return existing ? this._settleContactForm(existing, status) : false;
        if (status?.needed === true) {
          this._renderContactForm(typeof status.ref === "string" ? status.ref : null);
          return true;
        }
        if (existing) return this._settleContactForm(existing, status);
        if (status?.provided !== true) return false;
        this._body.appendChild(this._buildMessageElement("bot", "Thanks, the team has your email.").element);
        this._body.scrollTop = this._body.scrollHeight;
        return true;
      } catch (error) {
        // Optional extra, the chat works the same without it.
        return false;
      }
    }

    // Keeps a box while its hand-off still lacks an email, thanks the visitor once it has one, and drops
    // the box quietly when its hand-off is no longer the latest, so nobody is thanked for an email never saved.
    _settleContactForm(form, status) {
      // A submit in progress reports its own outcome, and a newer hand-off still needing an email takes the box over.
      if (form.querySelector(".contact-form__submit")?.disabled) return false;
      if (status?.needed === true && typeof status.ref === "string") {
        form.dataset.ref = status.ref;
        return false;
      }
      const ref = form.dataset.ref;
      let answered;
      if (ref && typeof status?.open === "boolean") {
        if (status.ref === ref && status.open) return false;
        answered = status.ref === ref;
      } else {
        if (status?.needed === true) return false;
        answered = true;
      }
      // At the bottom, not in the box's old spot, which sits above the message that gave the email.
      const hadFocus = form.contains(this.shadowRoot.activeElement);
      form.remove();
      if (answered) this._body.appendChild(this._buildMessageElement("bot", "Thanks, the team has your email.").element);
      if (hadFocus) (this._input.disabled ? this._panel : this._input).focus();
      this._body.scrollTop = this._body.scrollHeight;
      return true;
    }

    async _handleSubmit(event) {
      event.preventDefault();
      const text = this._input.value.trim();
      if (!text || this._input.disabled) return;

      // A restored draft can be submitted the instant the panel paints, well
      // before an in-flight history fetch resolves. Without this wait, the
      // visitor's message and the real history/greeting can append out of
      // order. Quick-reply chips and Resend don't need this, they can only
      // ever run after the history render that created/preceded them.
      // Disabling the composer for this wait too (reusing the same disabled
      // flag _sendAndStream already uses as its "in progress" signal) closes
      // the window where a second Enter press during the wait would read the
      // same still-pending promise and start a second concurrent send.
      if (this._historyPromise) {
        // Captured locally rather than re-read after the await: a bfcache
        // restore can replace this._historyPromise with a fresh one (see
        // _recoverFromBfcache) while this call is suspended waiting on the
        // OLD promise. Without this, resuming on the old promise would null
        // out the fresh one the restore just started, then immediately
        // append this draft and send, before the real post-restore history
        // load has rendered, producing an out-of-order transcript. Same
        // "only clear what you started with" guard already used for
        // _historyController just below in _loadHistory.
        const pendingHistory = this._historyPromise;
        this._input.disabled = true;
        this._sendButton.disabled = true;
        await pendingHistory;
        if (this._historyPromise === pendingHistory) this._historyPromise = null;
        this._input.disabled = false;
        this._sendButton.disabled = false;
      }

      // The wait above can resolve into a panel that isn't open anymore: if
      // closePanel() ran while pendingHistory was in flight, it aborts
      // _historyController and sets _historyPromise to null, but _loadHistory
      // swallows that abort internally rather than rejecting, so the await
      // above still resolves normally. Sending into a closed panel would
      // fire a real backend request the visitor can't see, and can race a
      // fresh history load from a subsequent reopen into an out-of-order or
      // duplicated transcript.
      if (this._panel.hidden) return;

      this._body.appendChild(this._buildMessageElement("visitor", text).element);
      this._input.value = "";
      this._writeStorage(DRAFT_STORAGE_KEY, "");

      await this._sendAndStream(text);
    }

    async _sendAndStream(message) {
      // Park focus on the panel itself before disabling the currently
      // focused control. Disabling a focused element runs the browser's
      // unfocusing steps and, with no explicit target, sends focus to
      // document.body, outside the shadow root, which silently breaks the
      // mobile focus trap for the whole pending window (up to 75s on a slow
      // stream). The panel has tabindex="-1" so it's a valid focus target
      // without becoming a spurious stop in the real Tab order.
      const hadComposerFocus =
        this.shadowRoot.activeElement === this._input || this.shadowRoot.activeElement === this._sendButton;
      this._input.disabled = true;
      this._sendButton.disabled = true;
      if (hadComposerFocus) this._panel.focus();
      // aria-busy suppresses live-region announcements for the whole reply
      // while it streams in, the log role would otherwise read every token
      // out loud as it lands. One announcement of the settled text once this
      // flips back to false, not a per-token flood. Interim "typing"/"still
      // working" cues go through the separate _statusRegion live region
      // instead, aria-busy would swallow those too if they lived in .panel__body.
      this._body.setAttribute("aria-busy", "true");
      this._statusRegion.textContent = "Support is typing";

      const { element: botElement, textNode: botTextNode } = this._buildMessageElement("bot", "");
      botElement.classList.add("message--pending");
      botElement.appendChild(this._buildTypingIndicator());
      this._body.appendChild(botElement);
      this._body.scrollTop = this._body.scrollHeight;

      try {
        const contactRequested = await this._streamReply(message, botElement, botTextNode);
        this._linkify(botTextNode);
        // The Worker files an email typed into the chat just after the reply, so look a moment later.
        if (!contactRequested && this._body.querySelector(".contact-form")) {
          setTimeout(() => this._checkContactStatus("after-reply"), 2000);
        }
      } catch (error) {
        console.error("tuff-chat-widget: failed to get a reply", error);
        // Closing the panel mid-reply aborts the stream before a box request could arrive.
        if (error?.name === "AbortError" && botTextNode.textContent) this._contactRecheck = true;
        if (botTextNode.textContent) {
          // Real content already arrived before the connection dropped, keep
          // it and offer a way to get the rest rather than throwing it away.
          this._linkify(botTextNode, true);
          this._addResendAffordance(botElement, message);
        } else if (error instanceof ReplyInProgressError) {
          this._showErrorState(botElement, botTextNode, ERROR_COPY.inProgress);
        } else if (error instanceof RateLimitError) {
          this._showErrorState(botElement, botTextNode, ERROR_COPY.busy);
        } else if (error?.name === "AbortError") {
          this._showErrorState(botElement, botTextNode, ERROR_COPY.timeout);
        } else {
          this._showErrorState(botElement, botTextNode, ERROR_COPY.unreachable);
        }
      } finally {
        this._input.disabled = false;
        this._sendButton.disabled = false;
        this._body.setAttribute("aria-busy", "false");
        this._statusRegion.textContent = "";
        // The email box is the next thing to fill in when it just appeared.
        const focusTarget = this._pendingFocus && this._pendingFocus.isConnected ? this._pendingFocus : this._input;
        this._pendingFocus = null;
        // A reply that finishes after the visitor closed the panel must not pull focus back into it.
        if (this._panel.classList.contains("is-open")) focusTarget.focus();
      }
    }

    _addResendAffordance(botElement, originalMessage) {
      const existing = botElement.querySelector(".resend-button");
      if (existing) existing.remove();

      const button = document.createElement("button");
      button.type = "button";
      button.className = "resend-button";
      button.textContent = "Resend";
      button.addEventListener("click", () => {
        // Guards against a stale Resend button (from an earlier failed
        // message) firing a second concurrent stream while a newer one is
        // still in flight, which would race for the shared composer
        // disabled/focus state. _input.disabled is already the widget's own
        // single "a send is in progress" signal, reuse it instead of adding
        // new state.
        // A history reload in flight is about to redraw the panel, so wait for it like the composer does.
        if (this._input.disabled || this._historyController) return;
        // Same reasoning as the quick-reply chip handler: removing the
        // currently-focused element (this button) sends focus to
        // document.body, outside the shadow root, unless something else is
        // focused first.
        this._input.focus();
        botElement.remove();
        this._sendAndStream(originalMessage);
      });
      botElement.appendChild(button);
    }

    _buildTypingIndicator() {
      const wrapper = document.createElement("span");
      wrapper.className = "typing-indicator";

      const dots = document.createElement("span");
      dots.className = "typing-dots";
      dots.setAttribute("aria-hidden", "true");
      dots.innerHTML = "<span></span><span></span><span></span>";
      wrapper.appendChild(dots);

      return wrapper;
    }

    _clearPendingIndicator(botElement) {
      const indicator = botElement.querySelector(".typing-indicator");
      if (indicator) indicator.remove();
      const status = botElement.querySelector(".status-line");
      if (status) status.remove();
    }

    _showStatusLine(botElement, text) {
      this._clearPendingIndicator(botElement);
      const status = document.createElement("span");
      status.className = "status-line";
      status.setAttribute("aria-hidden", "true");
      status.textContent = text;
      botElement.appendChild(status);
      this._statusRegion.textContent = text;
    }

    _showErrorState(botElement, botTextNode, message) {
      this._clearPendingIndicator(botElement);
      botElement.classList.remove("message--pending");
      botElement.classList.add("message--error");
      botTextNode.textContent = message;
    }

    _postChat(message, signal) {
      const body = { message, contact_box: true };
      if (this._accountEmail) body.account_email = this._accountEmail;
      if (this._productHandle) body.viewing_handle = this._productHandle;
      // An email typed while the box shows answers that box's hand-off.
      const boxRef = this._body.querySelector(".contact-form")?.dataset.ref;
      if (boxRef) body.contact_box_ref = boxRef;
      return fetch(`${API_BASE}/chat`, {
        method: "POST",
        signal,
        headers: { "content-type": "application/json", "x-session-id": this._sessionId },
        body: JSON.stringify(body),
      });
    }

    // Mirrors the server's own collectStreamedReply parsing (src/lib/sse.ts):
    // same buffer/split-on-blank-line/data-line approach, applied here to
    // text_delta chunks and Anthropic's in-band "error" event (which the
    // server forwards verbatim, everything except tool_use frames passes
    // through filterClientStream unchanged). Text lands in the DOM exactly
    // as the network delivers it, no typewriter animation layered on top.
    // Three timers guard the wait: soft (status line), hard (abort on
    // time-to-first-token), and an absolute cap that only starts once real
    // streaming begins. A stream that ends cleanly (done:true) without ever
    // emitting a text_delta, a pure tool-call turn or a moderation cut, is
    // treated as an error too so the bubble never freezes on typing dots
    // with no error shown and no way to recover.
    async _streamReply(message, botElement, textNode) {
      const controller = new AbortController();
      this._activeController = controller;
      let firstTokenReceived = false;
      let absoluteCapTimer = null;

      const softTimer = setTimeout(() => {
        this._showStatusLine(botElement, "Still working on your answer…");
      }, SOFT_TIMEOUT_MS);

      const hardTimer = setTimeout(() => {
        controller.abort();
      }, HARD_TIMEOUT_MS);

      try {
        let response = await this._postChat(message, controller.signal);
        this._syncSessionId(response);
        if (response.status === 429) {
          // A 429 here is either a genuine per-IP rate limit, or the
          // backend's per-session concurrency lock rejecting a second
          // in-flight turn (reply_in_progress). Only the first is worth a
          // silent retry, retrying a still-in-flight lock is close to
          // guaranteed to fail again immediately. The two share a status
          // code, so the body has to be read to tell them apart.
          const body = await response.clone().json().catch(() => null);
          if (body?.error === "reply_in_progress") {
            throw new ReplyInProgressError("Previous turn for this session is still being processed");
          }
          response = await this._postChat(message, controller.signal);
          this._syncSessionId(response);
        }
        if (response.status === 429) {
          const body = await response.clone().json().catch(() => null);
          if (body?.error === "reply_in_progress") {
            throw new ReplyInProgressError("Previous turn for this session is still being processed");
          }
          throw new RateLimitError("Rate limited after one silent retry");
        }
        if (!response.ok || !response.body) {
          throw new Error(`Chat request failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let text = "";
        let contactNeeded = false;
        let contactRef = null;

        // Shared by the loop below and the final-flush handling after it,
        // so the two can never drift into handling one SSE frame two
        // different ways.
        const processSseMessage = (rawMessage) => {
          const lines = rawMessage.split("\n");
          const dataLine = lines.find((line) => line.startsWith("data:"));
          if (!dataLine) return;

          const payload = dataLine.slice("data:".length).trim();
          if (!payload) return;

          let parsed;
          try {
            parsed = JSON.parse(payload);
          } catch {
            return;
          }

          // Cards come after the reply text, so a shopper reading at the bottom is kept there to see them.
          const eventLine = lines.find((line) => line.startsWith("event:"));
          const eventName = eventLine ? eventLine.slice("event:".length).trim() : "";
          if (eventName === "product_cards") {
            const stickToBottom = this._isNearBottom();
            this._renderProductCards(parsed.products, botElement);
            if (stickToBottom) this._body.scrollTop = this._body.scrollHeight;
            return;
          }
          // Sent after the reply when a hand-off went through with no email to reply to.
          if (eventName === "contact_needed") {
            contactNeeded = true;
            if (typeof parsed.ref === "string") contactRef = parsed.ref;
            return;
          }

          if (parsed.type === "error") {
            throw new Error(parsed.error?.message || "Upstream stream error");
          }

          if (parsed.type === "content_block_delta" && parsed.delta?.type === "text_delta") {
            if (!firstTokenReceived) {
              firstTokenReceived = true;
              clearTimeout(softTimer);
              clearTimeout(hardTimer);
              botElement.classList.remove("message--pending");
              this._clearPendingIndicator(botElement);
              absoluteCapTimer = setTimeout(() => controller.abort(), STREAM_ABSOLUTE_CAP_MS);
            }
            // Captured before mutating content: a visitor who has
            // scrolled up mid-stream to reread something shouldn't get
            // yanked back to the bottom on the very next token, only
            // someone already at (or very near) the bottom stays pinned
            // there as new text lands.
            const stickToBottom = this._isNearBottom();
            text += parsed.delta.text ?? "";
            textNode.textContent = stripBold(text);
            if (stickToBottom) this._body.scrollTop = this._body.scrollHeight;
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const messages = buffer.split("\n\n");
          buffer = messages.pop() ?? "";

          for (const rawMessage of messages) processSseMessage(rawMessage);
        }

        // The loop only processes a message once a trailing blank line has
        // arrived. If the upstream closes the stream without one after its
        // final data: line (Worker CPU-time cutoff, proxy truncation, no
        // final flush), that whole last frame is still sitting in buffer and
        // would otherwise be silently dropped. decoder.decode() with no
        // argument also flushes any partial multi-byte character (em dash,
        // curly quote, emoji) TextDecoder was still holding at the split.
        buffer += decoder.decode();
        if (buffer) processSseMessage(buffer);

        if (!firstTokenReceived && contactNeeded) {
          // Same words the Worker saves for a hand-off with no reply text.
          botElement.classList.remove("message--pending");
          this._clearPendingIndicator(botElement);
          textNode.textContent = "Leave your email in the box below and I'll pass this to our team so they can reply.";
        } else if (!firstTokenReceived) {
          throw new Error("Stream ended with no reply text");
        }
        if (contactNeeded) this._pendingFocus = this._renderContactForm(contactRef);
        return contactNeeded;
      } finally {
        clearTimeout(softTimer);
        clearTimeout(hardTimer);
        clearTimeout(absoluteCapTimer);
        if (this._activeController === controller) this._activeController = null;
      }
    }

    openPanel() {
      // A close that's still mid-transition (its own delayed hidden=true
      // hasn't fired yet) must not win a race against this reopen, or the
      // panel would go invisible again a moment after the visitor just
      // reopened it.
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
      this._panel.hidden = false;
      // Every other full-screen/drawer surface in this theme (cart drawer,
      // nav drawer) animates open rather than cutting instantly. Forces a
      // reflow between clearing [hidden] and adding the class that drives
      // the transition, otherwise the browser can coalesce both style
      // changes into one paint and there's no "from" state left to animate.
      void this._panel.offsetHeight;
      this._panel.classList.add("is-open");
      this._launcher.setAttribute("aria-expanded", "true");
      this._launcher.style.display = "none";
      this._syncModalState();
      // Covers reopening (or rotating) while the viewport is already
      // keyboard-shrunk; the focus listener alone wouldn't catch that,
      // since the input isn't newly focused in that case.
      this._syncViewportHeight();
      if (!this._historyLoaded) {
        this._historyLoaded = true;
        this._historyPromise = this._loadHistory();
      } else if (this._contactRecheck) {
        this._checkContactStatus("reopen").then((changed) => {
          if (changed) this._contactRecheck = false;
        });
      }
      this._input.focus();
      this._writeStorage(OPEN_STORAGE_KEY, "1");
    }

    // Klaviyo's sign-up form moves focus to its own teaser a moment after the launcher click minimises it.
    // Pull focus back once, unless the visitor moves on first. Launcher clicks only, never an automatic open.
    _guardOpenFocus() {
      if (this._releaseOpenFocusGuard) this._releaseOpenFocusGuard();
      const onFocusIn = (event) => {
        if (this._panel.hidden || event.target === this || this._input.disabled) return;
        release();
        this._input.focus();
      };
      const release = () => {
        clearTimeout(timer);
        document.removeEventListener("focusin", onFocusIn, true);
        document.removeEventListener("pointerdown", release, true);
        document.removeEventListener("keydown", release, true);
        if (this._releaseOpenFocusGuard === release) this._releaseOpenFocusGuard = null;
      };
      const timer = setTimeout(release, 1500);
      document.addEventListener("focusin", onFocusIn, true);
      document.addEventListener("pointerdown", release, true);
      document.addEventListener("keydown", release, true);
      this._releaseOpenFocusGuard = release;
    }

    closePanel() {
      if (this._panel.hidden) return;
      if (this._releaseOpenFocusGuard) this._releaseOpenFocusGuard();
      if (this._activeController) {
        this._activeController.abort();
        this._activeController = null;
      }
      // A history load that's still in flight when the panel closes would
      // otherwise keep running in the background: closePanel only aborted
      // _activeController (the /chat stream), never this one. An orphaned
      // /history fetch that later resolves after a bfcache freeze/restore
      // cycle can mutate _body while the panel is closed, and since
      // _historyLoaded was already optimistically set true when the fetch
      // started, the next real open wouldn't retry, silently showing
      // whatever the orphaned fetch rendered instead. Resetting
      // _historyLoaded here makes the next open fetch for real.
      if (this._historyController) {
        this._historyController.abort();
        this._historyController = null;
        this._historyLoaded = false;
        this._historyPromise = null;
      }
      // The panel still fully covers the viewport (position: fixed, opacity
      // fading) for the duration of the transition, so releasing the
      // inert/scroll-lock state immediately doesn't expose an interactive
      // gap underneath it, only the [hidden] attribute (which removes it
      // from layout entirely) needs to wait for the transition to finish.
      this._panel.classList.remove("is-open");
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      // Clear any timer a still-in-flight previous close already scheduled.
      // The guard above only blocks a second closePanel() call once
      // _panel.hidden has actually flipped true, so a double-tap inside this
      // same 180ms transition window would otherwise overwrite _closeTimer
      // with a second timeout and orphan the first one, which fires later
      // regardless and silently re-hides the panel even if the visitor
      // reopened it in the meantime.
      if (this._closeTimer) clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(
        () => {
          this._panel.hidden = true;
          this._closeTimer = null;
        },
        prefersReducedMotion ? 0 : 180,
      );
      this._panel.removeAttribute("aria-modal");
      this._setSiblingsInert(false);
      this._releaseScrollLockIfClear();
      this._launcher.setAttribute("aria-expanded", "false");
      this._launcher.style.display = "";
      this._launcher.focus();
      this._writeStorage(OPEN_STORAGE_KEY, "0");
    }

    // document.body.classList.remove('overflow-hidden') is a shared,
    // non-reference-counted lock across this whole theme (cart drawer, any
    // modal-dialog/custom-popup, the header nav drawer all use the same
    // class). Stripping it unconditionally, which the original code did both
    // on close and every time the panel opens on desktop, silently unlocks
    // background scroll out from under any of those if one happens to be
    // open at the same time. Only release when nothing else is still holding it.
    _releaseScrollLockIfClear() {
      if (!document.querySelector(OTHER_SCROLL_LOCK_SELECTOR)) {
        document.body.classList.remove("overflow-hidden");
      }
    }

    // WAI-ARIA's modal pattern calls for background content to be made
    // inert, not just visually covered, aria-modal alone is inconsistently
    // honored across screen reader/browser combinations for actually
    // restricting navigation into page content behind the panel.
    _setSiblingsInert(state) {
      // el === this is never true: the snippet always wraps this element in
      // a <div class="color-scheme-2"> (so the theme's color scheme
      // variables are available to inherit through the shadow boundary),
      // so the actual document.body child is that wrapper div, one level
      // above this element, not this element itself. Node.contains() covers
      // both "is this element" and "is an ancestor of this element", so it
      // correctly skips the wrapper instead of marking the widget's own
      // container (and everything inside it, including itself) inert the
      // moment the mobile modal opens.
      for (const el of document.body.children) {
        if (el.contains(this)) continue;
        el.toggleAttribute("inert", state);
      }
    }

    _syncModalState() {
      if (this._panel.hidden) return;

      if (this._mobileQuery.matches) {
        this._panel.setAttribute("aria-modal", "true");
        document.body.classList.add("overflow-hidden");
        this._setSiblingsInert(true);
      } else {
        this._panel.removeAttribute("aria-modal");
        this._setSiblingsInert(false);
        this._releaseScrollLockIfClear();
      }
    }

    // Sets --tuff-vvh from the real visible viewport height so the mobile
    // full-screen panel (CSS: height: var(--tuff-vvh, 100dvh)) can track the
    // on-screen keyboard, which 100dvh alone does not. Called on composer
    // focus and on every visualViewport resize; also worth calling once when
    // the panel opens in case the viewport is already keyboard-shrunk (a
    // rotation, or reopening while the keyboard was left up). Re-reads after
    // two short delays, same staggering the theme's own predictive-search.js
    // uses, because iOS animates the keyboard open/close rather than
    // resizing instantly, a single synchronous read can catch a stale
    // pre-animation value. this.style writes onto the host element (light
    // DOM), which custom properties inherit through the shadow boundary
    // from, the same way this file already reads theme tokens like
    // --temp-scrollbar-width across that boundary.
    _syncViewportHeight() {
      if (!window.visualViewport) return;
      this._applyViewportMetrics();
      // Re-pin to the bottom if the visitor was already there, the same
      // "stick to bottom unless they scrolled up" rule the streaming reply
      // already follows, so a keyboard open/close or a rotation doesn't
      // leave the newest message looking like it scrolled away on its own.
      if (this._isNearBottom()) this._body.scrollTop = this._body.scrollHeight;
      this._trackViewport();
    }

    // Writes both halves of the correction the mobile panel needs: its
    // height (the keyboard shrinks visualViewport, which 100dvh does not
    // track) and its top (iOS scrolls the visual viewport inside the layout
    // viewport, which position:fixed does not follow). See the .panel
    // mobile block for the full symptom.
    _applyViewportMetrics() {
      const viewport = window.visualViewport;
      if (!viewport) return;
      if (!this._mobileQuery.matches || this._panel.hidden) return;
      this.style.setProperty("--tuff-vvh", `${viewport.height}px`);
      this.style.setProperty("--tuff-vvt", `${viewport.offsetTop}px`);
    }

    // iOS animates the keyboard open over roughly 300ms and keeps firing
    // visualViewport events the whole way. The previous fixed 0/100/500ms
    // re-reads could only sample that animation at three points, so the
    // panel sat visibly misplaced in between, which is exactly what the
    // screen recording showed. Following it frame by frame closes the gap.
    // Deliberately bounded: the loop stops itself at 700ms, and the panel
    // closing or the breakpoint changing stops it sooner. This must never
    // become a permanent rAF loop.
    _trackViewport() {
      if (!window.visualViewport) return;
      const now = () => (window.performance ? performance.now() : Date.now());
      this._trackUntil = now() + 700;
      if (this._trackFrame) return;
      const step = () => {
        this._applyViewportMetrics();
        if (this._isNearBottom()) this._body.scrollTop = this._body.scrollHeight;
        if (now() < this._trackUntil && this._mobileQuery.matches && !this._panel.hidden) {
          this._trackFrame = requestAnimationFrame(step);
        } else {
          this._trackFrame = null;
        }
      };
      this._trackFrame = requestAnimationFrame(step);
    }

    // Zotabox adds its button with no event to listen for, so watch for it once, then follow its size.
    _watchCornerWidget() {
      const follow = (widget) => {
        this._scheduleClearanceSync();
        if (window.ResizeObserver) new ResizeObserver(() => this._scheduleClearanceSync()).observe(widget);
        widget.addEventListener("transitionend", () => this._scheduleClearanceSync());
        widget.addEventListener("animationend", () => this._scheduleClearanceSync());
      };
      const present = document.querySelector(CORNER_WIDGET_SELECTOR);
      if (present || !window.MutationObserver) {
        if (present) follow(present);
        return;
      }
      const observer = new MutationObserver(() => {
        const widget = document.querySelector(CORNER_WIDGET_SELECTOR);
        if (!widget) return;
        observer.disconnect();
        clearTimeout(giveUp);
        follow(widget);
      });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
      const giveUp = setTimeout(() => observer.disconnect(), 30000);
    }

    // Coalesces bursts of resize/observer callbacks into one measurement per
    // frame. Measuring reads layout, so doing it per event would force a
    // reflow on every one of them.
    _scheduleClearanceSync() {
      if (this._clearanceFrame) return;
      this._clearanceFrame = requestAnimationFrame(() => {
        this._clearanceFrame = null;
        this._syncClearanceNow();
      });
    }

    _syncClearanceNow() {
      const barHeight = Math.max(measureBottomBarHeight(), measureCornerWidgetReach());
      this.style.setProperty("--tuff-clearance-mobile", clearanceFor(barHeight));
      // Publish where the launcher's top edge lands so the theme's own
      // scroll-to-top button can stack above it instead of underneath it.
      // Measured live at 393x852 on 2026-08-19: the button sat at 802-842
      // and the launcher at 776-820, so the launcher covered its top-right
      // corner on every template without a buy bar. The rule that consumes
      // this lives in snippets/tuff-chat-widget.liquid. Read the launcher's
      // rendered height rather than assuming 44px, it grows if the heading
      // font loads late or the label ever gets longer, and fall back to 44
      // for the one case where it has no box: the panel is open, which
      // hides the launcher via display:none.
      const launcherHeight = Math.round(this._launcher.getBoundingClientRect().height) || 44;
      const base = barHeight > 0 ? `${barHeight}px` : "var(--px-mobile, 2rem)";
      document.documentElement.style.setProperty(
        "--tuff-scroll-top-bottom",
        `calc(${base} + ${LAUNCHER_GAP_PX * 2 + launcherHeight}px)`,
      );
    }

    // Used to decide whether an automatic scroll-to-bottom should fire:
    // always safe right after rendering fresh content (greeting, history, a
    // just-sent message), but during an active stream it should only keep
    // happening if the visitor hasn't deliberately scrolled up to reread
    // something, a stream can run up to STREAM_ABSOLUTE_CAP_MS (75s), long
    // enough that fighting a manual scroll on every token is a real problem.
    _isNearBottom() {
      return this._body.scrollHeight - this._body.scrollTop - this._body.clientHeight < 80;
    }

    _handlePanelKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closePanel();
        return;
      }

      if (!this._mobileQuery.matches) return;

      if (event.key === "Tab") {
        this._trapFocus(event);
      }
    }

    _trapFocus(event) {
      const focusable = Array.from(
        this._panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => !el.disabled);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this.shadowRoot.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define("tuff-chat-widget", TuffChatWidget);
})();
